import { FiEye } from "react-icons/fi";
import { BiChevronDown, BiChevronRight } from "react-icons/bi";
import React, { useState, useEffect, ReactNode, useRef } from "react";

interface CustomDropdownProps {
  options: {
    id: number;
    name: string;
    value: string;
    customerId: string;
    icon?: ReactNode;
  }[];
  onSelect: (selected: any) => void;
  optionHeading: string;
  dropdownIcon: boolean;
  bgColor: string;
  defaultValue?: string;
  width?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  onSelect,
  optionHeading,
  dropdownIcon,
  bgColor,
  defaultValue,
  width,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    defaultValue || ""
  );
  const [searchTerm, setSearchTerm] = useState<string>("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync with defaultValue changes (so external updates via query params pick the right option)
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null) {
      setSelectedOption(defaultValue);
    }
  }, [defaultValue]);

  const filteredOptions = options
    .filter((option) =>
      option.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(0, 5); // Display only the first 5 filtered options

  return (
    <div className='relative inline-block text-left' ref={dropdownRef}>
      {/* Dropdown Button */}
      <div style={{ width: width }}>
        <button
          type='button'
          className={`gap-[8px] outline-none inline-flex justify-between items-center w-full px-[14px] py-[8px] border-[1px] border-darkcolor-300 text-[16px] font-medium rounded-[8px] text-neutral-25 ${bgColor} focus:outline-none`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className='flex items-center text-[14px]'>
            {dropdownIcon && <FiEye className='mr-2' />}
            {selectedOption}
          </div>
          <BiChevronDown className='ml-2' />
        </button>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div className='border-[1px] border-darkcolor-400 origin-top-right absolute z-10 right-0 mt-[3px] w-full rounded-[8px] shadow-lg bg-darkcolor-700'>
          <div className=''>
            <div className='block px-4 py-2 text-[14px] cursor-auto border-b-[1px] border-darkcolor-400 text-neutral-25 bg-darkcolor-800 rounded-t-[8px]'>
              {optionHeading}
            </div>

            {/* Search Input */}
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 bg-darkcolor-800 text-neutral-25 border-b-[1px] border-darkcolor-400 w-full"
            />

            {filteredOptions.map((option, index) => (
              <div
                key={option?.id}
                className={`text-[16px] p-[10px] text-sm  hover:bg-darkcolor-600 hover:text-neutral-25 cursor-pointer text-neutral-25 flex justify-between bg-darkcolor-800 ${index === filteredOptions.length - 1 ? "rounded-b-[8px]" : ""
                  }`}
                onClick={() => {
                  setSelectedOption(option.value);
                  onSelect(option);
                  setIsOpen(!isOpen);
                }}
              >
                <div className='flex'>
                  {option?.icon}
                  <p className='ml-[8px]'>{option.name}</p>
                </div>
                <BiChevronRight size={20} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
